import type { BrowserDisplaySession, BrowserViewport } from "@cmmd-forge/contracts";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type FocusEvent as ReactFocusEvent,
  type FormEvent as ReactFormEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type UIEvent as ReactUIEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { cn } from "~/lib/utils";
import { resolveBrowserCaptureLayout, type BrowserCaptureLayout } from "./BrowserCanvas.logic";
import {
  isInteractiveBrowserScrollCaptureActive,
  mapBrowserKeyboardEvent,
  mapBrowserPointerToViewport,
  resolveBrowserDisplayEmptyLabel,
  resolveBrowserKeyboardDispatch,
  resolveInteractiveBrowserSurfaceLayout,
  shouldDeduplicateBrowserPointerFallback,
  shouldRouteInteractiveBrowserFocusedKey,
  shouldShowInteractiveBrowserEmptyState,
} from "./InteractiveBrowserSurface.logic";
import { BrowserLiveViewSurface } from "./BrowserLiveViewSurface";

interface InteractiveBrowserSurfaceProps {
  readonly tabId: string | null;
  readonly frameDataUrl: string | undefined;
  readonly viewport: BrowserViewport | undefined;
  readonly interactionEnabled: boolean;
  readonly selectionEnabled: boolean;
  readonly onPointerCommand: (command: {
    readonly type: "clickPoint" | "wheel";
    readonly x: number;
    readonly y: number;
    readonly deltaX?: number;
    readonly deltaY?: number;
    readonly button?: "left" | "middle" | "right";
  }) => void;
  readonly onKeyboardCommand: (command: {
    readonly type: "pressKey" | "typeText";
    readonly key?: string;
    readonly text?: string;
  }) => void;
  readonly onContainerResize?: (size: { width: number; height: number }) => void;
  readonly emptyLabel?: string;
  readonly display?: BrowserDisplaySession | undefined;
}

function getKeyboardCommandSignature(command: {
  readonly type: "pressKey" | "typeText";
  readonly key?: string;
  readonly text?: string;
}): string {
  return command.type === "pressKey"
    ? `pressKey:${command.key ?? ""}`
    : `typeText:${command.text ?? ""}`;
}

const SCROLL_CAPTURE_CENTER = 10_000;
const SCROLL_CAPTURE_SIZE = 20_000;

function mapPointerToViewport(input: {
  readonly event: { readonly clientX: number; readonly clientY: number };
  readonly bounds: DOMRect;
  readonly layout: BrowserCaptureLayout | null;
  readonly viewport: BrowserViewport | undefined;
  readonly display: BrowserDisplaySession | undefined;
}): { readonly x: number; readonly y: number } | null {
  const capturePoint = {
    x: input.event.clientX - input.bounds.left,
    y: input.event.clientY - input.bounds.top,
  };
  return mapBrowserPointerToViewport({
    point: capturePoint,
    layout: input.layout,
    viewport: input.viewport,
    display: input.display,
  });
}

function shouldRouteFocusedSurfaceKey(input: {
  readonly surface: HTMLDivElement | null;
  readonly target: EventTarget | null;
  readonly hasKeyboardOwnership: boolean;
}): boolean {
  const { surface, target, hasKeyboardOwnership } = input;
  if (!surface) {
    return false;
  }
  const surfaceContainsTarget = target instanceof Node && surface.contains(target);
  const activeElement = document.activeElement;
  const activeElementIsSurfaceOrDescendant =
    activeElement === surface || (activeElement instanceof Node && surface.contains(activeElement));
  const activeElementIsDocumentRoot =
    activeElement === document.body || activeElement === document.documentElement;
  return shouldRouteInteractiveBrowserFocusedKey({
    surfaceContainsTarget,
    hasKeyboardOwnership,
    activeElementIsSurfaceOrDescendant,
    activeElementIsDocumentRoot,
  });
}

function consumeKeyboardEvent(event: {
  readonly preventDefault?: () => void;
  readonly stopPropagation?: () => void;
  readonly stopImmediatePropagation?: () => void;
}): void {
  event.preventDefault?.();
  event.stopPropagation?.();
  event.stopImmediatePropagation?.();
}

export function InteractiveBrowserSurface(props: InteractiveBrowserSurfaceProps) {
  const {
    frameDataUrl,
    tabId,
    viewport,
    interactionEnabled,
    selectionEnabled,
    onPointerCommand,
    onKeyboardCommand,
    onContainerResize,
    emptyLabel = "Connecting live preview",
    display,
  } = props;
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const keyboardCaptureRef = useRef<HTMLInputElement | null>(null);
  const scrollCaptureRef = useRef<HTMLDivElement | null>(null);
  const scrollCapturePositionRef = useRef({
    left: SCROLL_CAPTURE_CENTER,
    top: SCROLL_CAPTURE_CENTER,
  });
  const scrollCaptureResettingRef = useRef(false);
  const handledKeyboardEventsRef = useRef<WeakSet<Event>>(new WeakSet());
  const recentKeyDownCommandRef = useRef<{
    readonly signature: string;
    readonly at: number;
  } | null>(null);
  const recentPointerCommandRef = useRef<{
    readonly button: number;
    readonly clientX: number;
    readonly clientY: number;
    readonly at: number;
  } | null>(null);
  const activeKeyDownCommandRef = useRef<{
    readonly signature: string;
    readonly at: number;
  } | null>(null);
  const focusedRef = useRef(false);
  const [surfaceSize, setSurfaceSize] = useState({ width: 0, height: 0 });
  const [focused, setFocused] = useState(false);
  const showEmptyState = shouldShowInteractiveBrowserEmptyState({ display });
  const scrollCaptureActive = isInteractiveBrowserScrollCaptureActive({
    interactionEnabled,
    selectionEnabled,
    tabId,
    display,
  });

  const claimKeyboardOwnership = useCallback(() => {
    focusedRef.current = true;
    setFocused(true);
  }, []);

  const releaseKeyboardOwnership = useCallback(() => {
    focusedRef.current = false;
    setFocused(false);
  }, []);

  const focusKeyboardCaptureTarget = useCallback(() => {
    const target = keyboardCaptureRef.current ?? surfaceRef.current;
    target?.focus({ preventScroll: true });
  }, []);

  const resetScrollCapture = useCallback(() => {
    const target = scrollCaptureRef.current;
    if (!target) {
      return;
    }
    scrollCaptureResettingRef.current = true;
    target.scrollLeft = SCROLL_CAPTURE_CENTER;
    target.scrollTop = SCROLL_CAPTURE_CENTER;
    scrollCapturePositionRef.current = {
      left: SCROLL_CAPTURE_CENTER,
      top: SCROLL_CAPTURE_CENTER,
    };
    window.setTimeout(() => {
      scrollCaptureResettingRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) {
      return;
    }
    const updateSize = () => {
      const bounds = surface.getBoundingClientRect();
      const size = { width: bounds.width, height: bounds.height };
      setSurfaceSize(size);
      onContainerResize?.(size);
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(surface);
    window.addEventListener("resize", updateSize);
    return () => {
      window.removeEventListener("resize", updateSize);
      observer.disconnect();
    };
  }, [onContainerResize]);

  useEffect(() => {
    if (!scrollCaptureActive) {
      return;
    }
    resetScrollCapture();
  }, [resetScrollCapture, scrollCaptureActive, surfaceSize.height, surfaceSize.width]);

  const captureLayout = useMemo(
    () =>
      resolveInteractiveBrowserSurfaceLayout({
        containerWidth: surfaceSize.width,
        containerHeight: surfaceSize.height,
        viewport,
        display,
      }) ??
      resolveBrowserCaptureLayout({
        containerWidth: surfaceSize.width,
        containerHeight: surfaceSize.height,
        viewport,
      }),
    [display, viewport, surfaceSize.height, surfaceSize.width],
  );

  const dispatchPointerLike = useCallback(
    (
      event: Pick<
        ReactMouseEvent<HTMLDivElement> | ReactPointerEvent<HTMLDivElement>,
        "button" | "clientX" | "clientY" | "preventDefault" | "stopPropagation"
      >,
      source: "click" | "mouse" | "pointer",
    ) => {
      if (!interactionEnabled || selectionEnabled) {
        return;
      }
      const recentPointerCommand = recentPointerCommandRef.current;
      if (
        shouldDeduplicateBrowserPointerFallback({
          source,
          button: event.button,
          clientX: event.clientX,
          clientY: event.clientY,
          recent: recentPointerCommand,
          now: Date.now(),
        })
      ) {
        return;
      }
      claimKeyboardOwnership();
      focusKeyboardCaptureTarget();
      if (!tabId) {
        return;
      }
      const bounds = surfaceRef.current?.getBoundingClientRect();
      if (!bounds) {
        return;
      }
      const point = mapPointerToViewport({
        event,
        bounds,
        layout: captureLayout,
        viewport,
        display,
      });
      if (!point) {
        return;
      }
      event.preventDefault();
      event.stopPropagation?.();
      recentPointerCommandRef.current = {
        button: event.button,
        clientX: event.clientX,
        clientY: event.clientY,
        at: Date.now(),
      };
      onPointerCommand({
        type: "clickPoint",
        x: point.x,
        y: point.y,
        button: event.button === 2 ? "right" : event.button === 1 ? "middle" : "left",
      });
    },
    [
      captureLayout,
      claimKeyboardOwnership,
      display,
      interactionEnabled,
      onPointerCommand,
      focusKeyboardCaptureTarget,
      selectionEnabled,
      tabId,
      viewport,
    ],
  );

  const dispatchPointer = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => dispatchPointerLike(event, "pointer"),
    [dispatchPointerLike],
  );

  const dispatchMouse = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => dispatchPointerLike(event, "mouse"),
    [dispatchPointerLike],
  );

  const dispatchClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => dispatchPointerLike(event, "click"),
    [dispatchPointerLike],
  );

  const onWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      if (!interactionEnabled || !tabId || selectionEnabled) {
        return;
      }
      const bounds = surfaceRef.current?.getBoundingClientRect();
      if (!bounds) {
        return;
      }
      const point = mapPointerToViewport({
        event,
        bounds,
        layout: captureLayout,
        viewport,
        display,
      });
      if (!point) {
        return;
      }
      event.preventDefault();
      onPointerCommand({
        type: "wheel",
        x: point.x,
        y: point.y,
        deltaX: event.deltaX,
        deltaY: event.deltaY,
      });
    },
    [
      captureLayout,
      display,
      interactionEnabled,
      onPointerCommand,
      selectionEnabled,
      tabId,
      viewport,
    ],
  );

  const onScrollCapture = useCallback(
    (event: ReactUIEvent<HTMLDivElement>) => {
      if (!scrollCaptureActive || !tabId) {
        return;
      }
      const target = event.currentTarget;
      if (scrollCaptureResettingRef.current) {
        scrollCapturePositionRef.current = {
          left: target.scrollLeft,
          top: target.scrollTop,
        };
        return;
      }
      const previous = scrollCapturePositionRef.current;
      const deltaX = target.scrollLeft - previous.left;
      const deltaY = target.scrollTop - previous.top;
      scrollCapturePositionRef.current = {
        left: target.scrollLeft,
        top: target.scrollTop,
      };
      if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) {
        return;
      }
      const bounds = surfaceRef.current?.getBoundingClientRect();
      if (!bounds) {
        resetScrollCapture();
        return;
      }
      const point = mapPointerToViewport({
        event: {
          clientX: bounds.left + bounds.width / 2,
          clientY: bounds.top + bounds.height / 2,
        },
        bounds,
        layout: captureLayout,
        viewport,
        display,
      });
      resetScrollCapture();
      if (!point) {
        return;
      }
      onPointerCommand({
        type: "wheel",
        x: point.x,
        y: point.y,
        deltaX,
        deltaY,
      });
    },
    [
      captureLayout,
      display,
      onPointerCommand,
      resetScrollCapture,
      scrollCaptureActive,
      tabId,
      viewport,
    ],
  );

  const dispatchKeyboardCommand = useCallback(
    (
      command: {
        readonly type: "pressKey" | "typeText";
        readonly key?: string;
        readonly text?: string;
      },
      event: {
        readonly preventDefault?: () => void;
        readonly stopPropagation?: () => void;
        readonly stopImmediatePropagation?: () => void;
      },
      eventType: "keydown" | "keypress" | "keyup" | "input",
      nativeEvent: Event | undefined,
    ) => {
      if (!interactionEnabled || !tabId || selectionEnabled) {
        return;
      }
      if (nativeEvent && handledKeyboardEventsRef.current.has(nativeEvent)) {
        return;
      }
      const signature = getKeyboardCommandSignature(command);
      const now = Date.now();
      if (nativeEvent) {
        handledKeyboardEventsRef.current.add(nativeEvent);
      }
      const dispatch = resolveBrowserKeyboardDispatch({
        commandSignature: signature,
        eventType,
        now,
        activeKeyDown: activeKeyDownCommandRef.current,
        recentKeyDown: recentKeyDownCommandRef.current,
      });
      activeKeyDownCommandRef.current = dispatch.nextActiveKeyDown;
      recentKeyDownCommandRef.current = dispatch.nextRecentKeyDown;
      if (dispatch.shouldConsume) {
        consumeKeyboardEvent(event);
      }
      if (!dispatch.shouldSend) {
        return;
      }
      onKeyboardCommand(command);
    },
    [interactionEnabled, onKeyboardCommand, selectionEnabled, tabId],
  );

  const onKeyboardEvent = useCallback(
    (
      event: Pick<
        ReactKeyboardEvent<HTMLDivElement> | KeyboardEvent,
        | "altKey"
        | "code"
        | "ctrlKey"
        | "key"
        | "metaKey"
        | "preventDefault"
        | "shiftKey"
        | "stopPropagation"
      > & {
        readonly nativeEvent?: Event;
        readonly stopImmediatePropagation?: () => void;
      },
      eventType: "keydown" | "keypress" | "keyup",
    ) => {
      const command = mapBrowserKeyboardEvent(event);
      if (!command) {
        return;
      }
      const nativeEvent = event.nativeEvent ?? (event instanceof Event ? event : undefined);
      dispatchKeyboardCommand(command, event, eventType, nativeEvent);
    },
    [dispatchKeyboardCommand],
  );

  const onKeyboardCaptureInput = useCallback(
    (event: ReactFormEvent<HTMLInputElement>) => {
      const target = event.currentTarget;
      const text = target.value;
      target.value = "";
      if (!text) {
        return;
      }
      for (const key of Array.from(text)) {
        const command = mapBrowserKeyboardEvent({ key });
        if (command) {
          dispatchKeyboardCommand(command, event, "input", undefined);
        }
      }
    },
    [dispatchKeyboardCommand],
  );

  useEffect(() => {
    if (!interactionEnabled || !tabId || selectionEnabled) {
      return;
    }
    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (
        !shouldRouteFocusedSurfaceKey({
          surface: surfaceRef.current,
          target: event.target,
          hasKeyboardOwnership: focusedRef.current,
        })
      ) {
        return;
      }
      onKeyboardEvent(event, "keydown");
    };
    const onWindowKeyPress = (event: KeyboardEvent) => {
      if (
        !shouldRouteFocusedSurfaceKey({
          surface: surfaceRef.current,
          target: event.target,
          hasKeyboardOwnership: focusedRef.current,
        })
      ) {
        return;
      }
      onKeyboardEvent(event, "keypress");
    };
    const onWindowKeyUp = (event: KeyboardEvent) => {
      if (
        !shouldRouteFocusedSurfaceKey({
          surface: surfaceRef.current,
          target: event.target,
          hasKeyboardOwnership: focusedRef.current,
        })
      ) {
        return;
      }
      onKeyboardEvent(event, "keyup");
    };
    window.addEventListener("keydown", onWindowKeyDown, { capture: true });
    window.addEventListener("keypress", onWindowKeyPress, { capture: true });
    window.addEventListener("keyup", onWindowKeyUp, { capture: true });
    return () => {
      window.removeEventListener("keydown", onWindowKeyDown, { capture: true });
      window.removeEventListener("keypress", onWindowKeyPress, { capture: true });
      window.removeEventListener("keyup", onWindowKeyUp, { capture: true });
    };
  }, [interactionEnabled, onKeyboardEvent, selectionEnabled, tabId]);

  useEffect(() => {
    if (!focused) {
      return;
    }
    const onWindowPointerDown = (event: PointerEvent) => {
      const surface = surfaceRef.current;
      if (!surface || !(event.target instanceof Node) || surface.contains(event.target)) {
        return;
      }
      releaseKeyboardOwnership();
    };
    window.addEventListener("pointerdown", onWindowPointerDown, { capture: true });
    return () => window.removeEventListener("pointerdown", onWindowPointerDown, { capture: true });
  }, [focused, releaseKeyboardOwnership]);

  const onBlur = useCallback(
    (event: ReactFocusEvent<HTMLDivElement>) => {
      const nextTarget = event.relatedTarget;
      const surface = surfaceRef.current;
      if (!(nextTarget instanceof Node)) {
        return;
      }
      if (surface?.contains(nextTarget)) {
        return;
      }
      if (nextTarget === document.body || nextTarget === document.documentElement) {
        return;
      }
      releaseKeyboardOwnership();
    },
    [releaseKeyboardOwnership],
  );

  return (
    <div
      ref={surfaceRef}
      className={cn(
        "relative h-full w-full overflow-hidden bg-muted/20 outline-none",
        selectionEnabled ? "cursor-crosshair" : "cursor-default",
      )}
      data-testid="interactive-browser-surface"
      tabIndex={interactionEnabled ? 0 : -1}
      data-focused={focused ? "true" : "false"}
      onPointerDown={dispatchPointer}
      onMouseDown={dispatchMouse}
      onClick={dispatchClick}
      onWheel={onWheel}
      onKeyDownCapture={(event) => onKeyboardEvent(event, "keydown")}
      onKeyDown={(event) => onKeyboardEvent(event, "keydown")}
      onKeyPressCapture={(event) => onKeyboardEvent(event, "keypress")}
      onKeyPress={(event) => onKeyboardEvent(event, "keypress")}
      onKeyUpCapture={(event) => onKeyboardEvent(event, "keyup")}
      onKeyUp={(event) => onKeyboardEvent(event, "keyup")}
      onFocus={claimKeyboardOwnership}
      onBlur={onBlur}
    >
      {frameDataUrl ? (
        <div aria-hidden="true" className="absolute inset-0">
          <BrowserLiveViewSurface
            url={null}
            tabId={null}
            frameDataUrl={frameDataUrl}
            viewport={viewport}
            interactionEnabled={false}
            selectionEnabled={false}
            onPointerCommand={() => undefined}
            onKeyboardCommand={() => undefined}
          />
        </div>
      ) : null}
      <div
        ref={scrollCaptureRef}
        aria-hidden="true"
        data-testid="interactive-browser-scroll-capture"
        className={cn(
          "absolute inset-0 overflow-scroll opacity-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          scrollCaptureActive ? "pointer-events-auto" : "pointer-events-none",
        )}
        onPointerDown={dispatchPointer}
        onMouseDown={dispatchMouse}
        onClick={dispatchClick}
        onScroll={onScrollCapture}
      >
        <div
          style={{
            width: SCROLL_CAPTURE_SIZE,
            height: SCROLL_CAPTURE_SIZE,
          }}
        />
      </div>
      <input
        ref={keyboardCaptureRef}
        aria-hidden="true"
        data-testid="interactive-browser-keyboard-capture"
        tabIndex={-1}
        defaultValue=""
        className="pointer-events-none absolute left-0 top-0 size-px opacity-0"
        onInput={onKeyboardCaptureInput}
      />
      {showEmptyState ? (
        <div
          className="absolute inset-4 flex items-center justify-center rounded-md border border-dashed border-border/60 bg-background/40 px-4 text-center text-xs text-muted-foreground"
          data-testid="interactive-browser-empty-state"
        >
          {resolveBrowserDisplayEmptyLabel({ display, fallback: emptyLabel })}
        </div>
      ) : null}
    </div>
  );
}
